/**
 * Insurance Requests (prototype v108 §9): every booking that bought the liability insurance add-on, from the client's
 * purchase through the talent's insurance form to the COI being bought and uploaded. Pure: rows in, what the page
 * shows out.
 *
 * Status:
 *   Awaiting Talent Form   the talent has not completed the form            → "Waiting on Talent"
 *   Ready to Purchase      form completed, COI not bought yet               → Confirm Purchased (COI file required)
 *   Purchased              marked bought (e.g. on the current console) but no COI file stored here
 *   COI Uploaded           bought, and the COI is stored on the booking     → Complete
 */
import { ROLES, ROLE_COLUMNS, ROLE_LABELS, isPlaceholderTalent } from '../booking/staffing.js'

export const INSURANCE_STATUSES = ['Awaiting Talent Form', 'Ready to Purchase', 'Purchased', 'COI Uploaded']
export const INSURANCE_ADDON_CODE = 'liability-insurance'

/** Did the client buy the liability insurance add-on? Checkout records it in personalization_data, later buys in booking_addons. */
export function hasInsuranceAddon(b) {
  const pd = (b?.personalization_data?.addons || []).some(a => a?.id === INSURANCE_ADDON_CODE || /insurance/i.test(String(a?.title || a?.name || '')))
  return pd || (b?.booking_addons || []).some(r => r?.addon?.code === INSURANCE_ADDON_CODE)
}

/** The talent's role on this booking, from the slot they sit in (Traci, Oct 2026: filled in by the system). */
export function talentRoleOn(b, talentId) {
  const role = talentId ? ROLES.find(r => b?.[ROLE_COLUMNS[r]] === talentId) : null
  return role ? { role, role_label: ROLE_LABELS[role] } : { role: null, role_label: null }
}

/**
 * Insured bookings the page would miss (Traci, Oct 2026: "All should show the full list"). A form row only exists
 * once the talent opens the form, so a booking with insurance bought and no form yet never showed. One row per
 * assigned talent with no form, or one "no talent yet" row; the L&P placeholder profile never owes a form.
 */
export function missingInsuranceRows(bookings, forms) {
  const have = new Set((forms || []).map(f => `${f.booking_id}:${f.talent_id}`))
  const anyForm = new Set((forms || []).map(f => f.booking_id))
  const out = []
  for (const b of bookings || []) {
    if (!b || b.is_test || String(b.booking_status || '').startsWith('cancel') || !hasInsuranceAddon(b)) continue
    const slots = ROLES.map(role => ({ role, id: b[ROLE_COLUMNS[role]] })).filter(s => s.id && !isPlaceholderTalent(s.id))
    if (slots.length === 0) { if (!anyForm.has(b.id)) out.push({ booking: b, talent_id: null, role: null }); continue }
    for (const s of slots) if (!have.has(`${b.id}:${s.id}`)) out.push({ booking: b, talent_id: s.id, role: s.role })
  }
  return out
}
export const UPCOMING_DAYS = 30
export const COI_TYPES = ['application/pdf', 'image/jpeg', 'image/png']
export const COI_MAX_BYTES = 10 * 1024 * 1024

const lower = v => String(v || '').toLowerCase()

/**
 * A request for talent who is no longer on the booking (Traci, 28 Sep 2026, Carolyn Sidell: 9 hours photo only, but
 * two talent on Insurance Requests after a reassignment). It drops off the list, the dashboard and the reminders,
 * and the booking gets a note naming the previous talent. A request already purchased stays: the COI was bought.
 */
export function isStaleInsurance(form, booking) {
  if (!form?.talent_id || !booking) return false
  if (form.coi_path || form.coi_purchased || ['purchased', 'resolved'].includes(lower(form.admin_action_status))) return false
  return ![booking.photographer_id, booking.second_photographer_id, booking.videographer_id, booking.second_videographer_id].includes(form.talent_id)
}

/** The booking note for a dropped request; the same words every time, so it is written once. */
export const staleInsuranceNote = name => `Insurance request dropped for ${name || 'the previous talent'}: no longer assigned to this booking.`

const noon = iso => Date.parse(String(iso).slice(0, 10) + 'T12:00:00Z')

export function insuranceStatusLabel(f) {
  if (f?.coi_path) return 'COI Uploaded'
  if (f?.coi_purchased || ['purchased', 'resolved'].includes(lower(f?.admin_action_status))) return 'Purchased'
  if (lower(f?.form_status) === 'completed') return 'Ready to Purchase'
  return 'Awaiting Talent Form'
}

/** What the Action column offers. */
export function insuranceAction(status) {
  if (status === 'Ready to Purchase') return 'confirm'
  if (status === 'Awaiting Talent Form') return 'waiting'
  return 'complete'
}

/**
 * @param rows  [{ id, booking_id, booking_ref, event_date, client_name, talent_name, status, ... }]
 * @param f     { tab: 'all'|'upcoming', status, q, today }
 */
export function filterInsurance(rows, { tab = 'all', status = 'all', q = '', today } = {}) {
  const needle = lower(q).trim()
  return (rows || [])
    .filter(r => {
      if (tab === 'upcoming') {
        const d = String(r.event_date || '').slice(0, 10)
        if (!d || !today || d < today) return false
        if ((noon(d) - noon(today)) / 86400000 > UPCOMING_DAYS) return false
      }
      if (status && status !== 'all' && r.status !== status) return false
      if (needle && ![r.client_name, r.talent_name, r.booking_id, r.booking_ref].some(v => lower(v).includes(needle))) return false
      return true
    })
    .sort((a, b) => String(a.event_date || '9999').localeCompare(String(b.event_date || '9999')))
}

/** Is this file acceptable as a COI? */
export function coiFileProblem(file) {
  if (!file) return 'Attach the COI file'
  if (!COI_TYPES.includes(file.mimetype || file.type)) return 'The COI must be a PDF, JPEG or PNG'
  if (Number(file.size) > COI_MAX_BYTES) return 'The COI file is larger than 10 MB'
  return null
}
